/**
 * Suite inference — catalog Connect + OpenAI-compatible vision chat.
 * Debit client: visionforge. Product UI never rents iron.
 */

import type { Connection, ConnectionKind } from "@forgesuite/gpu-browser";
import { getAccessToken } from "../suite/auth";

const DEFAULT_RELAY =
  (import.meta.env.VITE_RELAY_URL as string | undefined)?.replace(/\/$/, "") ||
  "https://bookforge-relay.forgesuiteui.app";

export function relayBase(): string {
  return DEFAULT_RELAY;
}

export type SuiteModelCapabilities = {
  textInput?: boolean;
  textOutput?: boolean;
  imageInput?: boolean;
  imageOutput?: boolean;
  audioInput?: boolean;
  audioOutput?: boolean;
  videoInput?: boolean;
  videoOutput?: boolean;
  multimodal?: boolean;
  tools?: boolean;
};

export type SuiteInfModel = {
  skuId: string;
  modelId: string;
  chatModelId?: string;
  name: string;
  modality: string;
  provider?: string;
  paramsB?: number;
  tags?: string[];
  /** From relay — prefer over hardcoding families when present. */
  capabilities?: SuiteModelCapabilities;
  inputModalities?: string[];
  outputModalities?: string[];
  priceInPerMtok?: number;
  priceOutPerMtok?: number;
  connectForRewrite?: boolean;
  offer: {
    id: string;
    pricePerHour: number;
    basePricePerHour?: number;
    available?: boolean;
    provider?: string;
  };
};

export type SuiteInstance = {
  id: string;
  status: string;
  modelRepoId?: string;
  modelName?: string;
  activeModelRepoId?: string;
  gpuName?: string;
  provider?: string;
  endpointUrl?: string;
  sessionToken?: string;
  billingActive?: boolean;
  error?: string;
};

export type InferenceSource =
  | "hf"
  | "cloudflare"
  | "civitai"
  | "forge_endpoint"
  | "custom";

export type InferenceSession = {
  instance: SuiteInstance;
  model: SuiteInfModel;
  baseUrl: string;
  apiKey: string;
  modelId: string;
  source?: InferenceSource;
};

function isCatalogKind(k: string | undefined): k is ConnectionKind {
  return k === "hf" || k === "cloudflare" || k === "civitai" || k === "forge_endpoint";
}

const SESSION_KEY = "visionforge.suite_session.v1";
const MODEL_KEY = "visionforge.suite_model.v1";

function authHeaders(): HeadersInit {
  const tok = getAccessToken();
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (tok) h.Authorization = `Bearer ${tok}`;
  return h;
}

/**
 * Multimodal chat models useful for VisionForge Ask (image in → text out).
 * Prefer relay `capabilities.imageInput` / tags; fall back to family heuristics
 * only when backend metadata is missing (older relays).
 */
export function isVisionLikely(m: SuiteInfModel): boolean {
  const mod = (m.modality || "").toLowerCase();
  // Image generators, video, TTS, ASR are not Ask VLMs
  if (
    /text-to-image|image-to-image|text-to-video|image-to-video|text-to-speech|speech|music|asr|automatic-speech/.test(
      mod,
    )
  ) {
    return false;
  }
  // Prefer chat SKUs; allow unknown modalities that look like VLMs
  if (mod && mod !== "text-generation" && mod !== "other" && !/vision|multimodal|vlm/.test(mod)) {
    return false;
  }

  // Authoritative metadata from forge-backend inference catalog
  if (m.capabilities?.imageInput === true || m.capabilities?.multimodal === true) {
    return true;
  }
  if (m.capabilities?.imageInput === false && m.capabilities?.multimodal === false) {
    // Explicitly non-vision when backend provided caps (pure text LLM)
    // Still allow tag override below for partial rows
  }
  if (m.inputModalities?.some((x) => String(x).toLowerCase() === "image")) {
    return true;
  }
  if ((m.tags || []).some((t) => /^(vision|multimodal|image-input|vlm)$/i.test(String(t)))) {
    return true;
  }

  // If backend sent capabilities without image-input, trust it for chat models
  if (m.capabilities && typeof m.capabilities.imageInput === "boolean") {
    return m.capabilities.imageInput;
  }

  const hay = `${m.name} ${m.modelId} ${m.chatModelId || ""} ${(m.tags || []).join(" ")} ${
    m.provider || ""
  }`.toLowerCase();

  // Explicit non-vision chat / voice
  if (
    /\b(tts|transcribe|whisper|voice|embedding|rerank|code-only)\b/.test(hay) ||
    /grok-voice|gpt-4o-transcribe|gpt-4o-mini-tts/.test(hay)
  ) {
    return false;
  }

  // Legacy family heuristics (older relays without capability fields)
  if (
    /gpt-4o|gpt-4\.1|gpt-5|o[1-4](?:-|$)|claude|gemini|pixtral|llava|moondream|internvl|idefics|cogvlm|fuyu|minicpm.?v|phi-3.?5?-?vision|phi-4.?vision|qwen.*vl|qwen2\.?5-?vl|llama.?3\.?2.?vision|florence|paligemma|vision|multimodal|vlm|gpt-image/.test(
      hay,
    )
  ) {
    if (/gpt-image|imagen|flux|sdxl|stable-diffusion|dall-?e|midjourney/.test(hay)) {
      return false;
    }
    return true;
  }

  return false;
}

/** Sort vision models: cheapest first, then name. */
export function sortVisionModels(models: SuiteInfModel[]): SuiteInfModel[] {
  return [...models].sort((a, b) => {
    const pa = Number(a.offer?.pricePerHour) || 0;
    const pb = Number(b.offer?.pricePerHour) || 0;
    if (pa !== pb) return pa - pb;
    return (a.name || "").localeCompare(b.name || "");
  });
}

export async function fetchInferenceCatalog(
  force = false,
  opts?: { visionOnly?: boolean },
): Promise<{
  models: SuiteInfModel[];
  total: number;
  matched?: number;
  byModality: Record<string, number>;
  byCapability?: Record<string, number>;
  note?: string;
  configured: boolean;
}> {
  // Prefer chat SKUs; vision=1 uses server capability tags when relay supports it
  const qs = new URLSearchParams({ modality: "text-generation" });
  if (force) qs.set("force", "1");
  if (opts?.visionOnly) qs.set("vision", "1");
  const res = await fetch(`${relayBase()}/api/inference/catalog?${qs}`, {
    headers: authHeaders(),
    signal: AbortSignal.timeout(60000),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(t || `Catalog HTTP ${res.status}`);
  }
  const data = (await res.json()) as {
    models?: SuiteInfModel[];
    total?: number;
    matched?: number;
    byModality?: Record<string, number>;
    byCapability?: Record<string, number>;
    note?: string;
    configured?: boolean;
    error?: string;
  };
  if (data.error && !data.models?.length) throw new Error(data.error);
  return {
    models: data.models || [],
    total: data.total ?? data.models?.length ?? 0,
    matched: data.matched,
    byModality: data.byModality || {},
    byCapability: data.byCapability,
    note: data.note,
    configured: Boolean(data.configured),
  };
}

export function loadPreferredModelId(): string | null {
  try {
    return localStorage.getItem(MODEL_KEY);
  } catch {
    return null;
  }
}

export function savePreferredModelId(id: string): void {
  try {
    localStorage.setItem(MODEL_KEY, id);
  } catch {
    /* ignore */
  }
}

export function loadSavedSession(): InferenceSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as InferenceSession;
  } catch {
    return null;
  }
}

export function saveSession(s: InferenceSession | null): void {
  try {
    if (!s) localStorage.removeItem(SESSION_KEY);
    else localStorage.setItem(SESSION_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

/**
 * Persist a catalog / Forge-endpoint Connect. Does not rent iron.
 */
export function applyCatalogConnection(
  conn: Connection,
  forgeApiUrl: string,
): InferenceSession {
  const source: InferenceSource = isCatalogKind(conn.kind) ? conn.kind : "custom";
  const relay = (forgeApiUrl || relayBase()).replace(/\/$/, "");
  const baseUrl = (conn.endpointUrl || `${relay}/v1`).replace(/\/$/, "");
  const apiKey = getAccessToken() || "";
  const modelId = conn.modelId || conn.label || "default";
  const model: SuiteInfModel = {
    skuId: conn.skuId || conn.id,
    modelId,
    name: conn.label || modelId,
    modality: conn.modality || "text-generation",
    provider: conn.kind,
    connectForRewrite: true,
    offer: {
      id: conn.id,
      pricePerHour: 0,
      available: true,
      provider: conn.kind,
    },
  };
  const instance: SuiteInstance = {
    id: conn.id,
    status: conn.ready ? "ready" : conn.status || "ready",
    modelRepoId: modelId,
    modelName: model.name,
    activeModelRepoId: modelId,
    gpuName: conn.kind,
    provider: conn.kind,
    endpointUrl: baseUrl,
    sessionToken: apiKey,
    billingActive: true,
  };
  const session: InferenceSession = {
    instance,
    model,
    baseUrl,
    apiKey,
    modelId,
    source,
  };
  saveSession(session);
  savePreferredModelId(modelId);
  return session;
}

function chatUrl(baseUrl: string): string {
  let u = baseUrl.replace(/\/$/, "");
  u = u.replace(/\/chat\/completions\/?$/i, "");
  if (/\/v1$/i.test(u)) return `${u}/chat/completions`;
  if (/\/v1\//i.test(u)) return `${u.replace(/\/$/, "")}/chat/completions`;
  return `${u}/v1/chat/completions`;
}

export async function suiteVisionChat(opts: {
  session?: InferenceSession | null;
  prompt: string;
  imageDataUrls: string[];
  modelId?: string;
}): Promise<{ text: string; usage?: unknown; backend: string }> {
  const session = opts.session || loadSavedSession();
  const token = session?.apiKey || getAccessToken();
  if (!token) throw new Error("Sign in and connect a suite engine first.");

  // Prefer instance endpoint; fall back to shared relay /v1 with JWT
  const bases: string[] = [];
  if (session?.baseUrl) bases.push(session.baseUrl);
  bases.push(`${relayBase()}/v1`);
  bases.push(relayBase());

  const content: unknown[] = [{ type: "text", text: opts.prompt.trim() }];
  for (const url of opts.imageDataUrls) {
    content.push({ type: "image_url", image_url: { url } });
  }

  const model =
    opts.modelId ||
    session?.modelId ||
    session?.model.chatModelId ||
    session?.model.modelId ||
    "suite-vision";

  const body = {
    model,
    messages: [{ role: "user", content }],
    max_tokens: 1200,
    temperature: 0.4,
    stream: false,
    client: "visionforge",
  };

  const errors: string[] = [];
  for (const base of bases) {
    const url = chatUrl(base);
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(180000),
      });
      if (res.status === 404) {
        errors.push(`${url}: 404`);
        continue;
      }
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        errors.push(`${url}: ${res.status} ${t.slice(0, 160)}`);
        if (res.status === 402 || res.status === 403) {
          throw new Error(t || "Insufficient suite credits or access denied.");
        }
        continue;
      }
      const data = (await res.json()) as {
        choices?: { message?: { content?: string | { text?: string }[] } }[];
        usage?: unknown;
        error?: { message?: string };
      };
      if (data.error?.message) throw new Error(data.error.message);
      const c = data.choices?.[0]?.message?.content;
      let text = "";
      if (typeof c === "string") text = c;
      else if (Array.isArray(c)) {
        text = c.map((p) => (typeof p === "object" && p && "text" in p ? String(p.text) : "")).join("");
      }
      if (!text.trim()) throw new Error("Empty vision response");
      return { text, usage: data.usage, backend: `suite · ${model}` };
    } catch (e) {
      if (e instanceof Error && /Insufficient suite|access denied/i.test(e.message)) throw e;
      errors.push(`${url}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  throw new Error(errors.join(" · ") || "Suite vision chat failed");
}

export async function stopSuiteSession(instanceId?: string): Promise<void> {
  const s = loadSavedSession();
  const id = instanceId || s?.instance.id;
  if (!id) {
    saveSession(null);
    return;
  }
  try {
    await fetch(`${relayBase()}/api/instances/${encodeURIComponent(id)}/stop`, {
      method: "POST",
      headers: authHeaders(),
      signal: AbortSignal.timeout(30000),
    });
  } catch {
    /* ignore */
  }
  saveSession(null);
}
